/**
 * What each suite of the platform is for, and what each of its screens does.
 *
 * Shown in two places: a sidebar group heading opens its `tagline`, and the
 * strip above every screen names the suite and the screen's `what`, with the
 * `summary` and the full screen list one click away.
 *
 * Written from what the screens actually render — their titles, sections and
 * controls — not from what an airport platform might be expected to do. When a
 * screen changes, its line here must change with it; a description that
 * promises a control the screen does not have is worse than none.
 *
 * The `id` is the default suite's in `navigation.ts`. A person can rename a
 * suite, move screens between suites and create their own: a renamed default
 * suite keeps its description by id, a suite somebody created has only its
 * name and its screens, and a screen keeps its own line wherever it is moved.
 */
import { defaultLayout, labelOf, SCREEN_BY_ROUTE, type NavLayout } from "./navigation";

export interface SuiteScreen {
  to: string;
  label: string;
  what: string;
}

export interface Suite {
  id: string;
  head: string;
  /** Two or three lines, for the sidebar. */
  tagline: string;
  /** What the suite is for, in a paragraph or two. */
  summary: string[];
  screens: SuiteScreen[];
}

export const SUITES: readonly Suite[] = [
  {
    id: "overview",
    head: "Overview",
    tagline: "The airport at a glance.",
    summary: ["Key measures against target, open work, and alarms, on one screen."],
    screens: [
      {
        to: "/",
        label: "Dashboard",
        what: "Key measures against target, open work, and open alerts.",
      },
      {
        to: "/overview",
        label: "Dynamic dashboard",
        what: "Every airport system as a live tile, with alarms.",
      },
    ],
  },
  {
    id: "executive",
    head: "Executive",
    tagline:
      "Decisions, risks, and performance for the leadership team.",
    summary: ["Key measures, big decisions, emergency readiness, expansion, regulation, and approvals. Every figure comes from the airport's own records."],
    screens: [
      {
        to: "/executive-kpis",
        label: "Executive KPIs",
        what: "Ten key measures, graded against target, with trends.",
      },
      {
        to: "/decision-assist",
        label: "Decision Room",
        what: "The big decisions on your desk, compared side by side, and the record of what was decided.",
      },
      {
        to: "/crisis",
        label: "Crisis Desk",
        what: "Emergency playbooks and how ready the airport is for each.",
      },
      {
        to: "/capacity-expansion",
        label: "Capacity & Expansion",
        what: "When the airport runs out of capacity, and what each expansion costs.",
      },
      {
        to: "/regulatory",
        label: "Regulatory Demands",
        what: "What the regulator can ask for, and when it's due.",
      },
      {
        to: "/benchmark",
        label: "Benchmark",
        what: "How the airport compares with its peers.",
      },
      {
        to: "/improvement",
        label: "Business Improvement",
        what: "Where the operation loses money, and the fixes ranked by payback.",
      },
      {
        to: "/green-program",
        label: "Green & LEED",
        what: "Carbon footprint, reduction plan, and LEED score.",
      },
      {
        to: "/approvals",
        label: "Approvals",
        what: "Requests and purchase orders waiting for approval.",
      },
      {
        to: "/sustainability",
        label: "Sustainability",
        what: "Carbon, energy, waste, noise and the demand ahead, against the sustainability targets.",
      },
    ],
  },
  {
    id: "finance-system",
    head: "Financial System",
    tagline:
      "Income, spending, and budget, read from SAP.",
    summary: ["Daily income and spending against budget, capital projects, and the live link to SAP. Sample figures are marked until SAP is connected."],
    screens: [
      {
        to: "/cash",
        label: "Cash & Bottom Line",
        what: "Daily income and spending against plan, and where to save.",
      },
      {
        to: "/financials",
        label: "Financials",
        what: "Revenue, cost, and capital against budget.",
      },
      {
        to: "/capex",
        label: "Capital Projects",
        what: "Capital projects: spend, progress, delays, and a recommendation for each.",
      },
      {
        to: "/finance-system",
        label: "The finance system",
        what: "The live connection to SAP, and what it returned.",
      },
    ],
  },
  {
    id: "airfield",
    head: "Aerodrome",
    tagline:
      "Runways, taxiways, lights, and markings.",
    summary: ["The airfield map, friction and rubber, markings, lights, docking guidance, utilities, and inspections, with their open work."],
    screens: [
      {
        to: "/drawing",
        label: "Interactive Map",
        what: "The surveyed airfield plan. Click anything for its record.",
      },
      {
        to: "/section-3d",
        label: "Section in 3D",
        what: "A section of the airfield in 3D, with its assets and work.",
      },
      {
        to: "/friction",
        label: "Friction testing",
        what: "Runway friction surveys and low-friction zones.",
      },
      {
        to: "/rubber",
        label: "Rubber removal",
        what: "Rubber removal plans for each touchdown zone.",
      },
      {
        to: "/markings",
        label: "Marking & visual aids",
        what: "Surface markings and signs, with their defects and work.",
      },
      {
        to: "/agl",
        label: "AGL & nav-aids",
        what: "Airfield lights and circuits, lamp life, and night rounds.",
      },
      {
        to: "/vdgs",
        label: "Visual docking",
        what: "Docking guidance units at every stand, and their condition.",
      },
      {
        to: "/electrical",
        label: "Electrical & utilities",
        what: "Electricity, water, generators, and drainage.",
      },
      {
        to: "/meters",
        label: "Meter register",
        what: "Every utility meter, its latest reading, and its status.",
      },
      {
        to: "/airfield-inspections",
        label: "Airfield inspections",
        what: "The daily airfield inspection and the work it found.",
      },
      {
        to: "/urgent-inspection",
        label: "Urgent Inspection",
        what: "The required inspection after bad weather, runway by runway.",
      },
      {
        to: "/instruments",
        label: "Instruments",
        what: "Weather, radar, and airfield readings as gauges.",
      },
    ],
  },
  {
    id: "flightops",
    head: "Flight operations",
    tagline:
      "Flights, stands, gates, and passenger information.",
    summary: ["Today's flights and delays, stand and gate planning, passenger queues, and the public flight boards."],
    screens: [
      {
        to: "/flights",
        label: "Today's flights",
        what: "Today's arrivals and departures, with delays.",
      },
      {
        to: "/aodb",
        label: "Flight record",
        what: "The airport's record of every flight, with predicted times.",
      },
      {
        to: "/flight-feeds",
        label: "Flight data feeds",
        what: "Live flight messages from airline and airport systems.",
      },
      {
        to: "/allocation",
        label: "Resource allocation",
        what: "Stands, gates, check-in desks, and belts in one plan.",
      },
      {
        to: "/passenger-flow",
        label: "Passenger flow",
        what: "Queue forecasts for check-in, security, and passport control.",
      },
      {
        to: "/flight-delays",
        label: "Delays",
        what: "Every delay, who reported it, and what it affects.",
      },
      {
        to: "/stands",
        label: "Stands and gates",
        what: "Where each flight parks. Drag a flight onto a stand to assign it.",
      },
      {
        to: "/turnarounds",
        label: "Turnarounds",
        what: "Ground time between arrival and departure, worst first.",
      },
      {
        to: "/fids",
        label: "Flight information",
        what: "The public flight board, plus gate and baggage belt screens.",
      },
    ],
  },
  {
    id: "estate",
    head: "Asset Management",
    tagline:
      "Every asset, its condition, and its maintenance.",
    summary: ["The asset register, condition and risk, preventive maintenance, inspections, lifecycle costs, and documents."],
    screens: [
      {
        to: "/asset-register",
        label: "Asset register",
        what: "Every asset, with its health, work, and documents.",
      },
      {
        to: "/facilities",
        label: "Airport facilities",
        what: "The airport's facilities and the work on each.",
      },
      {
        to: "/buildings",
        label: "Buildings",
        what: "Buildings, their condition, and open work.",
      },
      {
        to: "/plans",
        label: "Building plans",
        what: "Floor plans with rooms, open work, and findings.",
      },
      {
        to: "/landside",
        label: "Landside",
        what: "Landside facilities, inspections, and reports.",
      },
      {
        to: "/equipment",
        label: "Equipment",
        what: "Every vehicle and piece of equipment, its condition, and where it is.",
      },
      {
        to: "/asset-condition",
        label: "Condition and risk",
        what: "Asset health and risk, highest risk first.",
      },
      {
        to: "/systems",
        label: "Systems condition",
        what: "The condition of the airport's systems, graded.",
      },
      {
        to: "/facility-inspections",
        label: "Facility inspections",
        what: "Facility inspections by trade, and the work they raise.",
      },
      {
        to: "/asset-maintenance",
        label: "Maintenance plans",
        what: "Preventive maintenance plans: what's due and what's overdue.",
      },
      {
        to: "/asset-lifecycle",
        label: "Lifecycle and capital plan",
        what: "Asset age, remaining life, and a 10-year renewal plan.",
      },
      {
        to: "/documents",
        label: "Documents",
        what: "Drawings, manuals, plans, and forms.",
      },
    ],
  },
  {
    id: "hr",
    head: "Human Resources",
    tagline:
      "Staff, attendance, leave, and pay.",
    summary: ["A file for every employee, attendance and check-in, leave and letters, and labor costs under Saudi law."],
    screens: [
      {
        to: "/hr",
        label: "HR home",
        what: "Find anyone, see today's attendance, and handle requests.",
      },
      {
        to: "/hr-me",
        label: "My check-in",
        what: "Check in and out from your phone, and send requests to HR.",
      },
      {
        to: "/hr-people",
        label: "Workforce",
        what: "Everyone on staff, with their file.",
      },
      {
        to: "/hr-attendance",
        label: "Attendance",
        what: "Today's attendance for everyone.",
      },
      {
        to: "/hr-records",
        label: "Records registry",
        what: "Every staff file entry: leave, letters, reviews, and more.",
      },
      {
        to: "/hr-notifications",
        label: "Messages",
        what: "Texts and emails sent to staff about their requests.",
      },
      {
        to: "/hr-calculators",
        label: "Calculators",
        what: "End of service, leave balance, sick pay, and final settlement.",
      },
      {
        to: "/hr-reports",
        label: "HR reports",
        what: "HR reports, ready to print or export.",
      },
      {
        to: "/hr-cost",
        label: "Manpower cost",
        what: "The full monthly cost of each employee.",
      },
      {
        to: "/hr-labour",
        label: "Labour cost of work",
        what: "Labor cost of work orders.",
      },
      {
        to: "/hr-saudization",
        label: "Saudization",
        what: "Saudization rate against target, by department.",
      },
      {
        to: "/hr-access",
        label: "Access levels",
        what: "Who can see and do what in HR.",
      },
      {
        to: "/hr-rules",
        label: "Saudi labour rules",
        what: "The Saudi labor rules used in HR calculations.",
      },
    ],
  },
  {
    id: "engineering",
    head: "Engineering",
    tagline:
      "The engineer's tools and work.",
    summary: ["Maintenance jobs, camera-based surface checks, projects, and required tests."],
    screens: [
      {
        to: "/engineering",
        label: "Engineering",
        what: "The engineer's home: modules, current work, and quick actions.",
      },
      {
        to: "/visual-analytics",
        label: "Visual analytics",
        what: "Cracks, rubber, marking wear, and debris found in photos.",
      },
      {
        to: "/maintenance",
        label: "Maintenance",
        what: "All maintenance jobs, overdue first.",
      },
      {
        to: "/maintenance-reporting",
        label: "Maintenance reporting",
        what: "How maintenance is going, and what keeps breaking.",
      },
      {
        to: "/capital-projects",
        label: "Capital projects",
        what: "Capital projects from start to handover.",
      },
      {
        to: "/projects",
        label: "Project Screen",
        what: "The project portfolio: money, gates, and quality checks.",
      },
      {
        to: "/testing-records",
        label: "Testing records",
        what: "Required ICAO, GACA, and FAA tests and their results.",
      },
    ],
  },
  {
    id: "ops",
    head: "Operations control",
    tagline:
      "Daily operations and work orders.",
    summary: ["Work orders, alerts, inspections, the airfield map, stores, and documents. This is where daily work is created and tracked."],
    screens: [
      {
        to: "/operations",
        label: "Operations",
        what: "Runway, stand, and ground operations, graded.",
      },
      {
        to: "/passengers",
        label: "Passenger",
        what: "The passenger journey: queues, security, bags, and gates.",
      },
      {
        to: "/stakeholders",
        label: "Stakeholders",
        what: "Airlines, handlers, contractors, and agencies, and how they perform.",
      },
      {
        to: "/work-orders",
        label: "Work orders",
        what: "All work orders and their deadlines. Filter them, see statistics, or print a report.",
      },
      {
        to: "/inspection-platform",
        label: "Inspection platform",
        what: "Checklist inspections, findings, and reports.",
      },
      {
        to: "/assessments",
        label: "Assessments",
        what: "Assessments and tests: overdue, due, or failed.",
      },
      {
        to: "/alerts",
        label: "Alerts",
        what: "Open alerts. Click one to see why it fired or to create a work order.",
      },
      {
        to: "/intelligence-report",
        label: "Intelligence report",
        what: "What will break, when, and what it will cost.",
      },
      {
        to: "/d-brief",
        label: "D-Brief",
        what: "One page: the three biggest risks for the next 30 days.",
      },
      {
        to: "/library",
        label: "Library",
        what: "Controlled documents, with review and expiry dates.",
      },
      {
        to: "/logistics",
        label: "Store & Warehouse",
        what: "Stock on hand, parts reserved for jobs, and purchase orders.",
      },
      {
        to: "/airfield",
        label: "Dynamic map",
        what: "Work orders, alerts, and crews on the airfield map. Send a crew from here.",
      },
      {
        to: "/inspections",
        label: "Inspection records",
        what: "Past inspection rounds and their scores.",
      },
    ],
  },
  {
    id: "safety",
    head: "Safety & compliance",
    tagline:
      "Safety alarms, risks, and regulatory compliance.",
    summary: ["Safety alarms and incidents, the safety management system, and regulatory obligations with their evidence."],
    screens: [
      {
        to: "/safety",
        label: "Safety",
        what: "Safety alarms, active incidents, and emergency status.",
      },
      {
        to: "/sms",
        label: "SMS",
        what: "The safety management system: hazards, risks, and reports.",
      },
      {
        to: "/compliance",
        label: "Compliance",
        what: "Regulatory obligations, their status, and evidence.",
      },
      {
        to: "/compliance-framework",
        label: "Compliance framework",
        what: "Audit findings and what was done about them.",
      },
      {
        to: "/standards",
        label: "Standards library",
        what: "The ICAO, GACA, and FAA standards the airport follows.",
      },
    ],
  },
  {
    id: "cert",
    head: "Certification",
    tagline:
      "Certificates: valid, expiring, and expired.",
    summary: ["What is certified, what expires next, and the certificate for each asset."],
    screens: [
      {
        to: "/certification",
        label: "Certification overview",
        what: "What is certified, what has expired, and what's missing.",
      },
      {
        to: "/certification/assets",
        label: "By asset",
        what: "Every certifiable asset and the certificate it depends on, with its status and expiry.",
      },
      {
        to: "/certification/renewals",
        label: "Renewals",
        what: "Certificates expiring soon, and how long renewal takes.",
      },
      {
        to: "/certification/documents",
        label: "Certificates on file",
        what: "The certificate documents themselves, filed against the asset, with issue and expiry dates.",
      },
    ],
  },
  {
    id: "platform",
    head: "Platform",
    tagline:
      "Search, Sky, calls, and settings.",
    summary: ["Find anything, ask Sky, call colleagues, manage people and roles, and check the platform's health."],
    screens: [
      {
        to: "/intelligence",
        label: "Systems intelligence",
        what: "What's abnormal, what's failing, and what's coming.",
      },
      {
        to: "/diagnostics",
        label: "System diagnostics",
        what: "A health check of this platform: what's live and what's sample data.",
      },
      {
        to: "/search",
        label: "Search",
        what: "Find any page, record, asset, or document.",
      },
      {
        to: "/ask",
        label: "Ask Sky-IQ",
        what: "Ask Sky questions about the airport's records and documents.",
      },
      {
        to: "/administration",
        label: "Administration",
        what: "People, roles, and sign-in settings.",
      },
      {
        to: "/contacts",
        label: "Contact directory",
        what: "Every contact at contractors, agencies, airlines, and departments.",
      },
      {
        to: "/notify-rules",
        label: "Who is told",
        what: "Who gets a text, email, or app message about each work order.",
      },
      {
        to: "/calls",
        label: "Staff calls",
        what: "Voice and video calls with colleagues.",
      },
      {
        to: "/call-policy",
        label: "Who can call whom",
        what: "Who can call whom, and who can use video.",
      },
      {
        to: "/migration",
        label: "Replacing a system",
        what: "Replacing the old system, screen by screen.",
      },
      {
        to: "/settings",
        label: "Settings",
        what: "Theme, menu, Sky's voice, and the platform connection.",
      },
    ],
  },
  {
    id: "operations",
    head: "Deep thinking",
    tagline:
      "Plans and decisions, with the reasons behind them.",
    summary: ["Ask a question, plan this year's maintenance, find a window for work, and check whether the forecasts hold."],
    screens: [
      {
        to: "/think",
        label: "Ask",
        what: "Plain questions about the work, answered by the platform.",
      },
      {
        to: "/decide",
        label: "Decide the program",
        what: "This year's maintenance plan, the cost of waiting, and what-ifs.",
      },
      {
        to: "/window",
        label: "Find a window",
        what: "When work can happen, given traffic and weather.",
      },
      {
        to: "/trust",
        label: "Can we trust it",
        what: "Whether the forecasts and records can be trusted.",
      },
    ],
  },
];

const WHAT: ReadonlyMap<string, string> = new Map(
  SUITES.flatMap((x) => x.screens.map((sc) => [sc.to, sc.what] as const)),
);

/** A screen's one line, wherever the person has put it. */
export function whatOf(to: string): string {
  return WHAT.get(to) ?? "";
}

/**
 * The suite and screen a path belongs to, as this person has arranged them.
 * A detail page belongs to its list — /work-orders/WO-7 and /work-orders/report
 * are Work orders — by the longest matching prefix.
 */
export function suiteForPath(
  pathname: string,
  layout: NavLayout = defaultLayout(),
): { suite: Suite; screen: SuiteScreen } | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  let best: { groupId: string; to: string } | null = null;
  for (const group of layout.groups) {
    for (const to of group.screens) {
      const hit = to === "/" ? path === "/" : path === to || path.startsWith(`${to}/`);
      if (hit && (!best || to.length > best.to.length)) best = { groupId: group.id, to };
    }
  }
  if (!best) return null;
  const group = layout.groups.find((g) => g.id === best.groupId)!;
  const described = SUITES.find((x) => x.id === group.id);
  const suite: Suite = {
    id: group.id,
    head: group.name,
    tagline: described?.tagline ?? "",
    summary: described?.summary ?? [],
    screens: group.screens
      .filter((to) => SCREEN_BY_ROUTE.has(to))
      .map((to) => ({ to, label: labelOf(layout, to), what: whatOf(to) })),
  };
  return { suite, screen: suite.screens.find((sc) => sc.to === best.to)! };
}
